"use client";

import { interpolateAll } from "flubber";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { roundCorners } from "svg-round-corners";
export const defaultAnimation = {
  hidden: {
    y: 20,
    rotateX: -90,
    opacity: 0.5,
    transition: {
      bounce: 0,
      delayChildren: 0.5,
    },
  },
  visible: {
    y: 0,
    rotateX: 0,
    opacity: 1,
    transition: {
      stiffness: 1000,
      bounce: 0,
      duration: 0.4,
      delayChildren: 1.0,
    },
  },
  exit: {
    y: -20,
    rotateX: 90,
    opacity: 0.5,
    transition: {
      bounce: 0,
      delayChildren: 0.5,
    },
  },
};

export const bodyAnimation = {
  hidden: {
    pathLength: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      delayChildren: 0.5,
    },
  },
  visible: (i: number) => {
    const delay = i * 0.1;
    return {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: {
          delay,
          type: "spring" as const,
          duration: 0.3,
          bounce: 0,
        },
        opacity: { delay, duration: 0.2 },
      },
    };
  },
  exit: {
    pathLength: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      delayChildren: 0.2,
    },
  },
};

const extensionlogoAnimation = {
  hidden: {
    pathLength: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      delayChildren: 0.5,
    },
  },
  visible: (i: number) => {
    const delay = i * 0.03;
    return {
      pathLength: 1,
      opacity: 1,
      transition: {
        pathLength: { delay, type: "spring", duration: 1, bounce: 0 },
        opacity: { delay, duration: 1 },
      },
    };
  },
  exit: {
    pathLength: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      delayChildren: 0.5,
    },
  },
};

const extensionlogoTextAnimation = {
  hidden: {
    pathLength: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      delayChildren: 0.5,
    },
  },
  visible: (i: number) => {
    const delay = i * 0.05;
    return {
      pathLength: 1,
      opacity: 1,
      transition: {
        opacity: { delay, duration: 0.1 },
      },
    };
  },
  exit: {
    pathLength: 0,
    opacity: 0,
    transition: {
      bounce: 0,
      pathLength: { type: "spring", duration: 0.1, bounce: 0 },
      opacity: { duration: 0.1 },
    },
  },
};

export const BODY = ({ className = "" }: { className?: string }) => {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: <explanation>
    <motion.svg
      className={className}
      width="29"
      height="29"
      viewBox="0 0 29 29"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <motion.rect
        y="1.69043"
        width="2.39059"
        height="11.4332"
        rx="1.19529"
        transform="rotate(-45 0 1.69043)"
        fill="white"
        layoutId="pill1"
        variants={bodyAnimation}
        custom={1}
      />
      <motion.rect
        x="26.8579"
        y="0.655029"
        width="2.39059"
        height="11.4332"
        rx="1.19529"
        transform="rotate(45 26.8579 0.655029)"
        fill="white"
        layoutId="pill2"
        variants={bodyAnimation}
        custom={2}
      />
      <motion.rect
        x="28.5483"
        y="26.8579"
        width="2.39059"
        height="11.4332"
        rx="1.19529"
        transform="rotate(135 28.5483 26.8579)"
        fill="white"
        layoutId="pill3"
        variants={bodyAnimation}
        custom={3}
      />
      <motion.rect
        x="2.34558"
        y="28.5483"
        width="2.39059"
        height="11.4332"
        rx="1.19529"
        transform="rotate(-135 2.34558 28.5483)"
        fill="white"
        layoutId="pill4"
        variants={bodyAnimation}
        custom={4}
      />
    </motion.svg>
  );
};

const BoutiqueBodySVG = ({ className }: { className?: string }) => (
  // biome-ignore lint/a11y/noSvgWithoutTitle: <explanation>
  <svg
    width="125"
    height="122"
    viewBox="0 0 125 122"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      x="7.28418"
      y="42.1152"
      width="10.2998"
      height="49.2601"
      rx="5.14992"
      transform="rotate(-135 7.28418 42.1152)"
      fill="white"
    />
    <rect
      x="81.8838"
      y="8.10254"
      width="10.2998"
      height="49.2601"
      rx="5.14992"
      transform="rotate(-45 81.8838 8.10254)"
      fill="white"
    />
    <rect
      x="89.2832"
      y="120.999"
      width="10.2998"
      height="49.2601"
      rx="5.14992"
      transform="rotate(-135 89.2832 120.999)"
      fill="white"
    />
    <rect
      y="86.2832"
      width="10.2998"
      height="49.2601"
      rx="5.14992"
      transform="rotate(-45 0 86.2832)"
      fill="white"
    />
  </svg>
);

const BoutiqueBrainSVG = ({ className }: { className?: string }) => (
  <svg
    width="125"
    height="123"
    viewBox="0 0 125 123"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      y="65.0557"
      width="7.86735"
      height="37.6265"
      rx="3.93368"
      transform="rotate(-90 0 65.0557)"
      fill="white"
    />
    <rect
      x="60.0918"
      y="0.146484"
      width="7.86735"
      height="37.6265"
      rx="3.93367"
      fill="white"
    />
    <rect
      x="125"
      y="57.1885"
      width="7.86735"
      height="37.6265"
      rx="3.93368"
      transform="rotate(90 125 57.1885)"
      fill="white"
    />
    <rect
      x="67.9585"
      y="122.098"
      width="7.86735"
      height="37.6265"
      rx="3.93367"
      transform="rotate(180 67.9585 122.098)"
      fill="white"
    />
  </svg>
);

const BoutiqueResilienceSVG = ({ className }: { className?: string }) => (
  <svg
    width="118"
    height="118"
    viewBox="0 0 118 118"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect
      width="10.0487"
      height="48.0588"
      rx="5.02433"
      transform="matrix(0.707101 -0.707112 0.707101 0.707112 0 8.10547)"
      fill="white"
    />
    <rect
      width="10.0487"
      height="48.0588"
      rx="5.02433"
      transform="matrix(0.707101 0.707112 -0.707101 0.707112 109.895 0.754883)"
      fill="white"
    />
    <rect
      width="10.0487"
      height="48.0588"
      rx="5.02433"
      transform="matrix(-0.707101 0.707112 -0.707101 -0.707112 118 110.895)"
      fill="white"
    />
    <rect
      width="10.0487"
      height="48.0588"
      rx="5.02433"
      transform="matrix(-0.707101 -0.707112 0.707101 -0.707112 7.85986 118)"
      fill="white"
    />
  </svg>
);

const BoutiqueLogo = ({ className }: { className?: string }) => (
  <svg
    width="83"
    height="36"
    viewBox="0 0 83 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <g clipPath="url(#clip0_275_1541)">
      <path
        d="M22.8372 17.9591C22.8372 21.2621 22.8434 24.5652 22.8311 27.8702C22.8291 28.6311 22.8904 29.4042 23.8496 29.3796C24.6841 29.3592 24.8804 28.6331 24.8825 27.9132C24.8886 21.2724 24.8886 14.6315 24.8825 7.99069C24.8825 7.26872 24.68 6.55699 23.8414 6.5304C22.9599 6.50381 22.8291 7.20328 22.8311 7.94774C22.8434 11.2855 22.8372 14.6233 22.8372 17.9611V17.9591Z"
        fill="white"
      />
      <path
        d="M39.8152 17.9509C39.8152 14.5436 39.7947 11.1383 39.8315 7.73093C39.8397 7.02532 39.6025 6.55697 38.9398 6.53243C38.2076 6.50584 37.954 6.98442 37.9561 7.73706C37.9806 14.5497 37.9806 21.3623 37.9561 28.177C37.954 28.8806 38.1831 29.351 38.8478 29.3755C39.58 29.4021 39.8397 28.9256 39.8315 28.1709C39.7968 24.7656 39.8172 21.3582 39.8172 17.9509H39.8152Z"
        fill="white"
      />
      <path
        d="M8.09411 27.363C5.87913 27.363 3.66415 27.4019 1.45122 27.3426C0.620863 27.3201 0.0113871 27.6432 0.00116102 28.3713C-0.0111103 29.1015 0.590185 29.4348 1.42055 29.4287C5.88322 29.3919 10.3479 29.4042 14.8106 29.4185C15.5408 29.4205 16.0889 29.3142 16.095 28.4041C16.1012 27.5103 15.5919 27.3487 14.8413 27.3569C12.5936 27.3774 10.3439 27.3651 8.09411 27.3651V27.363Z"
        fill="white"
      />
      <path
        d="M8.11876 18.9776C10.3317 18.9776 12.5446 18.9633 14.7596 18.9858C15.5266 18.994 16.1135 18.8917 16.0951 17.9182C16.0788 16.9999 15.5061 16.9222 14.7923 16.9242C10.3317 16.9345 5.87106 16.9365 1.41043 16.9242C0.680287 16.9222 0.132167 17.0285 0.126032 17.9387C0.119896 18.8324 0.627111 18.994 1.37771 18.9858C3.62541 18.9654 5.87311 18.9776 8.1208 18.9776H8.11876Z"
        fill="white"
      />
      <path
        d="M8.05238 8.54495C10.3308 8.54495 12.6071 8.53472 14.8855 8.55109C15.589 8.55722 16.0656 8.40383 16.1003 7.56938C16.1331 6.7247 15.6095 6.50791 14.953 6.50586C10.3982 6.4895 5.84354 6.4895 1.28883 6.50586C0.644584 6.50791 0.0964639 6.70016 0.125097 7.55302C0.151685 8.37929 0.61186 8.55722 1.3236 8.55109C3.56721 8.53268 5.81081 8.54495 8.05442 8.54495H8.05238Z"
        fill="white"
      />
      <path
        d="M73.0764 21.6691C72.7471 21.9493 72.287 22.1538 72.1806 22.4749C72.0415 22.9044 71.9986 23.3994 72.6306 23.7471C74.7024 24.8863 76.7312 26.1052 78.756 27.3242C79.5189 27.7823 80.114 27.5491 80.429 26.864C80.7194 26.232 80.3206 25.7575 79.7132 25.418C77.9993 24.4568 76.314 23.4444 74.6103 22.4627C74.1604 22.2029 73.6859 21.982 73.0744 21.6691H73.0764Z"
        fill="white"
      />
      <path
        d="M73.0447 14.2C73.4231 14.0487 73.646 13.9894 73.8403 13.8789C75.8303 12.7315 77.8407 11.6128 79.7837 10.3857C80.1498 10.1546 80.5097 9.39783 80.3932 9.03787C80.1886 8.40385 79.5751 8.10729 78.8388 8.54702C76.7772 9.77415 74.6829 10.9461 72.652 12.2202C72.3452 12.4125 72.1468 13.0547 72.1959 13.4515C72.2307 13.7378 72.7584 13.9628 73.0467 14.2H73.0447Z"
        fill="white"
      />
      <path
        d="M57.2047 12.797C57.1352 12.7111 56.9797 12.3736 56.718 12.2161C54.6298 10.9706 52.5109 9.77617 50.4248 8.52858C49.6906 8.08886 49.1322 8.46518 48.9502 9.00716C48.8295 9.36917 49.1588 10.1382 49.5167 10.3652C51.4842 11.6148 53.5172 12.7601 55.5379 13.9239C56.3703 14.4025 57.1802 13.9791 57.2068 12.7949L57.2047 12.797Z"
        fill="white"
      />
      <path
        d="M57.3792 22.8901C57.1358 22.5097 57.0049 22.0516 56.7472 21.9616C56.3811 21.8348 55.8494 21.8471 55.516 22.0311C53.5056 23.1417 51.536 24.332 49.5399 25.4691C48.9222 25.8209 48.6032 26.3118 48.9406 26.9397C49.2474 27.5082 49.7689 27.7802 50.4664 27.3671C52.5566 26.1236 54.6713 24.921 56.7595 23.6755C56.9988 23.5323 57.1338 23.2112 57.3792 22.8901Z"
        fill="white"
      />
      <path
        d="M74.3064 32.7808C74.153 32.4045 74.0896 32.1836 73.9791 31.9893C72.8461 30.0218 71.7069 28.0604 70.5677 26.097C70.2425 25.5387 69.8907 25.0887 69.136 25.4998C68.4263 25.8864 68.3077 26.4038 68.6963 27.0706C69.8928 29.1178 71.0647 31.1794 72.3266 33.1837C72.5209 33.4925 73.1631 33.6889 73.5619 33.6398C73.8482 33.605 74.0712 33.0733 74.3084 32.7808H74.3064Z"
        fill="white"
      />
      <path
        d="M56.4351 33.7114C56.521 33.6398 56.8278 33.4721 57.0119 33.2144C57.3964 32.6704 57.7298 32.0895 58.0611 31.5087C58.9385 29.9748 59.8241 28.447 60.6626 26.8926C60.998 26.2709 60.8078 25.7534 60.1186 25.4569C59.4866 25.1849 59.1205 25.4773 58.8199 25.9948C57.6459 28.0134 56.4576 30.0239 55.2919 32.0486C54.8133 32.881 55.2469 33.6909 56.4351 33.7114Z"
        fill="white"
      />
      <path
        d="M56.4344 2.19858C55.2093 2.21903 54.8371 3.04735 55.3546 3.93906C56.4569 5.84317 57.547 7.75545 58.6535 9.65751C58.9951 10.2424 59.3202 10.8703 60.2058 10.404C61.1057 9.93156 60.8582 9.28937 60.4922 8.6533C59.3468 6.65716 58.1913 4.66716 57.0071 2.69352C56.8476 2.42764 56.5162 2.26812 56.4324 2.19653L56.4344 2.19858Z"
        fill="white"
      />
      <path
        d="M72.9204 2.20068C72.8365 2.26 72.4909 2.37453 72.3498 2.61382C71.092 4.7388 69.8751 6.88833 68.6336 9.02355C68.2368 9.70461 68.6009 10.0666 69.1306 10.4061C69.7503 10.8029 70.188 10.4818 70.4764 9.99912C71.6892 7.96412 72.8713 5.91072 74.0453 3.85322C74.5218 3.01877 74.0739 2.21295 72.9204 2.20273V2.20068Z"
        fill="white"
      />
      <path
        d="M51.1676 16.9303C49.9793 16.9303 48.789 16.9406 47.6007 16.9262C46.8788 16.9181 46.5536 17.2453 46.5577 17.9693C46.5617 18.7056 46.9238 18.9878 47.6314 18.9837C50.01 18.9715 52.3886 18.9755 54.7672 18.9796C55.4769 18.9796 55.9677 18.6831 55.9555 17.9407C55.9432 17.2003 55.4401 16.9221 54.7365 16.9283C53.5482 16.9385 52.3579 16.9303 51.1696 16.9303H51.1676Z"
        fill="white"
      />
      <path
        d="M78.1069 18.9776C79.2257 18.9776 80.3465 18.9674 81.4652 18.9817C82.1994 18.9899 82.7578 18.7444 82.7455 17.9345C82.7353 17.1962 82.2281 16.9242 81.5245 16.9283C79.2175 16.9365 76.9105 16.9385 74.6035 16.9283C73.902 16.9242 73.3866 17.1839 73.3743 17.9263C73.36 18.7301 73.9061 18.9919 74.6444 18.9837C75.7979 18.9694 76.9514 18.9796 78.1049 18.9796L78.1069 18.9776Z"
        fill="white"
      />
      <path
        d="M65.5857 4.53014C65.5857 3.26824 65.6184 2.00634 65.5612 0.746482C65.5489 0.474468 65.1992 -0.01025 65.0274 -2.3854e-05C64.5958 0.0224736 64.0927 0.182001 63.7777 0.464242C63.5609 0.658538 63.5528 1.14939 63.5487 1.50731C63.5282 3.55253 63.5384 5.59775 63.5405 7.64297C63.5405 7.84545 63.5466 8.05406 63.5937 8.2504C63.7409 8.8517 63.9823 9.30574 64.7595 9.16871C65.461 9.04395 65.6021 8.61854 65.5919 8.00702C65.5734 6.84738 65.5857 5.68979 65.5857 4.53014Z"
        fill="white"
      />
      <path
        d="M65.587 31.4535C65.587 30.2263 65.5767 28.9992 65.591 27.7721C65.6013 26.9581 65.1575 26.6902 64.4253 26.7515C63.4865 26.8292 63.5745 27.6146 63.5601 28.1893C63.5111 30.302 63.5336 32.4168 63.5499 34.5295C63.552 34.8199 63.691 35.1083 63.7381 35.4008C63.869 36.2004 64.5153 35.9939 64.9427 35.9121C65.1984 35.863 65.542 35.4171 65.5583 35.1328C65.6258 33.9098 65.587 32.6806 65.587 31.4514V31.4535Z"
        fill="white"
      />
      <path
        d="M31.3751 18.9776C32.3936 18.9776 33.4142 18.9613 34.4327 18.9838C35.1506 18.9981 35.713 18.8999 35.7294 17.9877C35.7457 17.0756 35.1526 16.9406 34.4675 16.9365C32.3609 16.9242 30.2543 16.9365 28.1477 16.9304C27.3849 16.9304 26.939 17.2658 26.9288 18.0593C26.9206 18.8733 27.5403 18.949 28.113 18.9674C29.199 19.0042 30.287 18.9776 31.3751 18.9776Z"
        fill="white"
      />
      <path
        d="M22.8372 17.9591C22.8372 14.6213 22.8454 11.2835 22.8311 7.94566C22.8291 7.2012 22.9599 6.50173 23.8414 6.52832C24.68 6.55287 24.8804 7.26665 24.8825 7.98861C24.8886 14.6294 24.8886 21.2703 24.8825 27.9111C24.8825 28.631 24.6861 29.3571 23.8496 29.3775C22.8925 29.4021 22.8291 28.629 22.8311 27.8682C22.8434 24.5651 22.8372 21.2621 22.8372 17.957V17.9591Z"
        fill="black"
      />
      <path
        d="M39.8153 17.9509C39.8153 21.3582 39.7948 24.7635 39.8296 28.1709C39.8378 28.9256 39.578 29.4021 38.8458 29.3755C38.1832 29.351 37.9521 28.8826 37.9541 28.177C37.9807 21.3644 37.9807 14.5517 37.9541 7.73706C37.9521 6.98442 38.2057 6.50584 38.9379 6.53243C39.6005 6.55697 39.8378 7.02532 39.8296 7.73093C39.7928 11.1362 39.8132 14.5436 39.8132 17.9509H39.8153Z"
        fill="black"
      />
      <path
        d="M8.09405 27.363C10.3438 27.363 12.5915 27.3773 14.8412 27.3548C15.5918 27.3487 16.1011 27.5082 16.095 28.402C16.0888 29.3121 15.5407 29.4185 14.8106 29.4164C10.3479 29.4021 5.88316 29.3898 1.42049 29.4266C0.590129 29.4328 -0.00912064 29.0994 0.00110547 28.3693C0.0133768 27.6412 0.622853 27.318 1.45117 27.3405C3.6641 27.3998 5.88112 27.361 8.09405 27.361V27.363Z"
        fill="black"
      />
      <path
        d="M8.11889 18.9776C5.8712 18.9776 3.6235 18.9633 1.3758 18.9858C0.6252 18.9919 0.11594 18.8304 0.124121 17.9387C0.130256 17.0285 0.678376 16.9222 1.40852 16.9242C5.86915 16.9385 10.3298 16.9365 14.7904 16.9242C15.5042 16.9242 16.0769 16.9999 16.0932 17.9182C16.1116 18.8917 15.5246 18.9919 14.7577 18.9858C12.5448 18.9654 10.3318 18.9776 8.11685 18.9776H8.11889Z"
        fill="black"
      />
      <path
        d="M8.05248 8.54495C5.80887 8.54495 3.56526 8.53268 1.32165 8.55109C0.609911 8.55722 0.151781 8.37929 0.123148 7.55302C0.0945146 6.70016 0.640589 6.50791 1.28688 6.50586C5.84159 6.4895 10.3963 6.4895 14.951 6.50586C15.6075 6.50791 16.1311 6.7247 16.0984 7.56938C16.0657 8.40383 15.5871 8.55518 14.8835 8.55109C12.6072 8.53472 10.3288 8.54495 8.05043 8.54495H8.05248Z"
        fill="black"
      />
      <path
        d="M73.0744 21.6691C73.6859 21.9841 74.1604 22.2029 74.6103 22.4627C76.314 23.4444 77.9993 24.4547 79.7132 25.418C80.3206 25.7596 80.7194 26.2341 80.429 26.864C80.114 27.5471 79.5189 27.7823 78.756 27.3242C76.7292 26.1052 74.7024 24.8863 72.6306 23.7471C71.9986 23.3994 72.0415 22.9024 72.1806 22.4749C72.2849 22.1538 72.7471 21.9473 73.0764 21.6691H73.0744Z"
        fill="black"
      />
      <path
        d="M73.0448 14.2C72.7564 13.9627 72.2287 13.7378 72.194 13.4514C72.1469 13.0526 72.3433 12.4125 72.65 12.2202C74.6809 10.946 76.7753 9.77413 78.8368 8.547C79.5752 8.10728 80.1867 8.40588 80.3912 9.03785C80.5078 9.39781 80.1458 10.1545 79.7817 10.3857C77.8388 11.6128 75.8304 12.7295 73.8383 13.8789C73.644 13.9914 73.4211 14.0486 73.0427 14.2H73.0448Z"
        fill="black"
      />
      <path
        d="M57.2048 12.797C57.1782 13.9791 56.3683 14.4045 55.5359 13.926C53.5152 12.7622 51.4823 11.6148 49.5148 10.3673C49.1569 10.1402 48.8276 9.37124 48.9482 9.00924C49.1303 8.46725 49.6886 8.09093 50.4229 8.53065C52.509 9.77824 54.6278 10.9727 56.716 12.2182C56.9798 12.3757 57.1332 12.7131 57.2028 12.799L57.2048 12.797Z"
        fill="black"
      />
      <path
        d="M57.3782 22.8901C57.1327 23.2112 56.9977 23.5323 56.7584 23.6755C54.6703 24.9231 52.5555 26.1257 50.4653 27.3671C49.7679 27.7802 49.2484 27.5082 48.9396 26.9397C48.6001 26.3118 48.9212 25.8209 49.5388 25.4691C51.535 24.332 53.5045 23.1437 55.515 22.0311C55.8483 21.8471 56.3821 21.8348 56.7462 21.9616C57.0039 22.0495 57.1348 22.5077 57.3782 22.8901Z"
        fill="black"
      />
      <path
        d="M74.3065 32.7808C74.0692 33.0733 73.8442 33.605 73.5599 33.6398C73.1611 33.6889 72.5189 33.4925 72.3246 33.1837C71.0627 31.1794 69.8908 29.1178 68.6944 27.0706C68.3058 26.4038 68.4244 25.8864 69.1341 25.4998C69.8888 25.0887 70.2406 25.5387 70.5657 26.097C71.7049 28.0604 72.8441 30.0218 73.9772 31.9893C74.0897 32.1836 74.151 32.4065 74.3044 32.7808H74.3065Z"
        fill="black"
      />
      <path
        d="M56.436 33.7114C55.2477 33.6909 54.8121 32.8831 55.2927 32.0486C56.4585 30.0239 57.6468 28.0134 58.8207 25.9948C59.1214 25.4773 59.4875 25.1849 60.1195 25.4569C60.8087 25.7534 60.9989 26.2709 60.6635 26.8926C59.8249 28.447 58.9394 29.9768 58.062 31.5087C57.7306 32.0875 57.3973 32.6704 57.0128 33.2144C56.8307 33.4721 56.5219 33.6398 56.436 33.7114Z"
        fill="black"
      />
      <path
        d="M56.4347 2.19861C56.5186 2.26815 56.8519 2.42972 57.0094 2.6956C58.1916 4.66924 59.3492 6.65924 60.4945 8.65538C60.8585 9.29144 61.106 9.93364 60.2081 10.4061C59.3226 10.8704 58.9974 10.2445 58.6558 9.65958C57.5494 7.75752 56.4593 5.84524 55.3569 3.94114C54.8394 3.04738 55.2096 2.22111 56.4368 2.20065L56.4347 2.19861Z"
        fill="black"
      />
      <path
        d="M72.9234 2.20065C74.0769 2.21088 74.5228 3.0167 74.0483 3.85115C72.8743 5.90864 71.6942 7.96205 70.4794 9.99704C70.191 10.4797 69.7533 10.8008 69.1336 10.404C68.6039 10.0645 68.2419 9.70253 68.6366 9.02147C69.8781 6.88626 71.095 4.73673 72.3528 2.61174C72.4939 2.3745 72.8395 2.25792 72.9234 2.19861V2.20065Z"
        fill="black"
      />
      <path
        d="M51.1657 16.9303C52.354 16.9303 53.5443 16.9365 54.7326 16.9283C55.4361 16.9221 55.9393 17.2003 55.9515 17.9407C55.9638 18.6831 55.473 18.9817 54.7633 18.9796C52.3847 18.9755 50.0061 18.9694 47.6275 18.9837C46.9198 18.9878 46.5578 18.7076 46.5537 17.9693C46.5497 17.2453 46.8748 16.9181 47.5968 16.9262C48.7851 16.9406 49.9754 16.9303 51.1637 16.9303H51.1657Z"
        fill="black"
      />
      <path
        d="M78.1069 18.9776C76.9533 18.9776 75.7998 18.9674 74.6463 18.9817C73.906 18.9899 73.3619 18.7281 73.3763 17.9243C73.3885 17.1819 73.9039 16.9242 74.6054 16.9263C76.9124 16.9365 79.2195 16.9365 81.5265 16.9263C82.23 16.9242 82.7372 17.1942 82.7475 17.9325C82.7577 18.7424 82.2014 18.9879 81.4672 18.9797C80.3484 18.9654 79.2276 18.9756 78.1089 18.9756L78.1069 18.9776Z"
        fill="black"
      />
      <path
        d="M65.5865 4.53014C65.5865 5.68979 65.5722 6.84943 65.5926 8.00702C65.6028 8.61854 65.4638 9.046 64.7602 9.16871C63.983 9.30574 63.7417 8.8517 63.5944 8.2504C63.5474 8.05611 63.5412 7.8475 63.5412 7.64297C63.5372 5.59775 63.529 3.55253 63.5494 1.50731C63.5535 1.14735 63.5617 0.656493 63.7785 0.464242C64.0935 0.182001 64.5966 0.0245188 65.0281 -2.3854e-05C65.1999 -0.01025 65.5497 0.474468 65.5619 0.746482C65.6192 2.00429 65.5865 3.26824 65.5885 4.53014H65.5865Z"
        fill="black"
      />
      <path
        d="M65.587 31.4534C65.587 32.6806 65.6258 33.9097 65.5583 35.1348C65.542 35.4191 65.1984 35.865 64.9427 35.9141C64.5153 35.9979 63.869 36.2024 63.7381 35.4028C63.691 35.1082 63.552 34.8219 63.5499 34.5315C63.5336 32.4188 63.5111 30.304 63.5601 28.1913C63.5745 27.6145 63.4865 26.8312 64.4253 26.7535C65.1575 26.6922 65.6013 26.958 65.591 27.7741C65.5767 29.0012 65.587 30.2283 65.587 31.4555V31.4534Z"
        fill="black"
      />
      <path
        d="M31.3751 18.9776C30.287 18.9776 29.199 19.0042 28.113 18.9674C27.5403 18.9469 26.9206 18.8712 26.9288 18.0593C26.937 17.2657 27.3828 16.9283 28.1477 16.9303C30.2543 16.9344 32.3609 16.9242 34.4675 16.9365C35.1547 16.9406 35.7478 17.0755 35.7294 17.9877C35.713 18.8999 35.1485 19.0001 34.4327 18.9837C33.4142 18.9633 32.3936 18.9776 31.3751 18.9776Z"
        fill="black"
      />
    </g>
    <defs>
      <clipPath id="clip0_275_1541">
        <rect width="82.7477" height="36" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const BoutiqueLogoFull = ({ className }: { className?: string }) => {
  return (
    <svg
      width="238"
      height="24"
      viewBox="0 0 238 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath="url(#clip0_418_1423)">
        <path
          d="M172.34 4.67603H181.555C181.703 4.67603 181.835 4.73407 181.951 4.83942C182.056 4.94692 182.11 5.08237 182.11 5.23931C182.11 5.39626 182.056 5.53171 181.951 5.63706C181.835 5.74455 181.703 5.80045 181.555 5.80045H172.34C172.183 5.80045 172.052 5.7424 171.945 5.63706C171.839 5.52956 171.786 5.39411 171.786 5.23931C171.786 5.08452 171.839 4.94692 171.945 4.83942C172.052 4.73192 172.183 4.67603 172.34 4.67603Z"
          fill="white"
        />
        <path
          d="M172.34 11.4204H181.555C181.703 11.4204 181.835 11.4785 181.951 11.586C182.056 11.6935 182.11 11.8332 182.11 11.988C182.11 12.1428 182.056 12.2804 181.951 12.3857C181.835 12.4932 181.703 12.5491 181.555 12.5491H172.34C172.183 12.5491 172.052 12.4911 171.945 12.3857C171.839 12.2782 171.786 12.1428 171.786 11.988C171.786 11.8332 171.839 11.6913 171.945 11.586C172.052 11.4785 172.183 11.4204 172.34 11.4204Z"
          fill="white"
        />
        <path
          d="M181.953 18.337C182.06 18.4445 182.114 18.5799 182.114 18.7347C182.114 18.8895 182.06 19.0271 181.953 19.1325C181.839 19.24 181.708 19.2959 181.559 19.2959H172.345C172.188 19.2959 172.057 19.2378 171.949 19.1325C171.844 19.025 171.79 18.8895 171.79 18.7347C171.79 18.5799 171.844 18.4423 171.949 18.337C172.057 18.2295 172.188 18.1736 172.345 18.1736H181.559C181.708 18.1736 181.839 18.2316 181.953 18.337Z"
          fill="white"
        />
        <path
          d="M198.262 18.5153C198.344 18.7002 198.329 18.8765 198.209 19.0463C198.101 19.2183 197.942 19.3 197.74 19.3H190.486C190.329 19.3 190.198 19.242 190.09 19.1366C189.985 19.0291 189.927 18.8937 189.927 18.7389C189.927 18.5841 189.981 18.4465 190.09 18.3411C190.198 18.2336 190.329 18.1756 190.486 18.1756H196.888L191.888 6.63464L186.536 18.9603C186.433 19.1861 186.263 19.2979 186.018 19.2979C185.773 19.2979 185.87 19.2764 185.793 19.2441C185.649 19.1818 185.554 19.0786 185.496 18.9367C185.442 18.7926 185.442 18.6486 185.509 18.5045L191.361 5.00283C191.464 4.77708 191.632 4.66528 191.879 4.66528C192.126 4.66528 192.294 4.77708 192.389 5.00283L198.254 18.5045V18.5174H198.262"
          fill="white"
        />
        <path
          d="M211.725 19.2958H202.233C202.079 19.2958 201.945 19.2377 201.84 19.1324C201.732 19.0249 201.674 18.8894 201.674 18.7346V5.23077C201.674 5.07382 201.728 4.93837 201.84 4.83303C201.945 4.72553 202.079 4.66748 202.233 4.66748C202.388 4.66748 202.521 4.72553 202.629 4.83303C202.734 4.94052 202.788 5.07597 202.788 5.23077V18.1735H211.717C211.872 18.1735 212.005 18.2315 212.11 18.3369C212.218 18.4444 212.276 18.5798 212.276 18.7346C212.276 18.8894 212.218 19.027 212.11 19.1324C212.005 19.2399 211.872 19.2958 211.717 19.2958L211.725 19.2915"
          fill="white"
        />
        <path
          d="M217.444 5.79406H212.837C212.68 5.79406 212.549 5.73601 212.441 5.62851C212.336 5.52101 212.282 5.38557 212.282 5.23077C212.282 5.07597 212.336 4.93837 212.441 4.83303C212.549 4.72553 212.68 4.66748 212.837 4.66748H223.165C223.314 4.66748 223.445 4.72553 223.561 4.83303C223.666 4.94052 223.72 5.07597 223.72 5.23077C223.72 5.38557 223.666 5.52316 223.561 5.62851C223.445 5.73601 223.314 5.79406 223.165 5.79406H218.558V18.7368C218.558 18.8937 218.504 19.0292 218.399 19.1345C218.291 19.242 218.16 19.2979 218.003 19.2979C217.846 19.2979 217.715 19.2399 217.608 19.1345C217.502 19.027 217.444 18.8916 217.444 18.7368V5.79406Z"
          fill="white"
        />
        <path
          d="M164.48 11.5967C164.366 11.4892 164.229 11.4333 164.087 11.4333H159.58C159.426 11.4333 159.292 11.4914 159.183 11.5967C159.075 11.7042 159.017 11.8354 159.017 11.9945C159.017 12.1536 159.075 12.2869 159.183 12.3922C159.288 12.4997 159.421 12.5578 159.58 12.5578H164.087C164.229 12.5578 164.368 12.4997 164.48 12.3922C164.588 12.2847 164.646 12.1536 164.646 11.9945C164.646 11.8354 164.588 11.7021 164.48 11.5967Z"
          fill="white"
        />
        <path
          d="M157.351 19.1346C157.458 19.0185 157.514 18.8809 157.514 18.7326V5.23946C157.514 5.08252 157.458 4.94707 157.351 4.83742C157.243 4.72992 157.112 4.67188 156.953 4.67188C156.794 4.67188 156.661 4.72992 156.553 4.83742C156.446 4.94492 156.39 5.08037 156.39 5.23946V18.7326C156.39 18.8766 156.446 19.0207 156.553 19.1346C156.661 19.2421 156.792 19.3002 156.953 19.3002C157.114 19.3002 157.243 19.2421 157.351 19.1346Z"
          fill="white"
        />
        <path
          d="M167.109 19.1346C167.215 19.0185 167.273 18.8809 167.273 18.7326V5.23946C167.273 5.08252 167.215 4.94707 167.109 4.83742C167.002 4.72992 166.871 4.67188 166.709 4.67188C166.548 4.67188 166.417 4.72992 166.312 4.83742C166.204 4.94492 166.146 5.08037 166.146 5.23946V18.7326C166.146 18.8766 166.204 19.0207 166.312 19.1346C166.417 19.2421 166.55 19.3002 166.709 19.3002C166.868 19.3002 167.002 19.2421 167.109 19.1346Z"
          fill="white"
        />
        <path
          d="M235.207 11.5967C235.093 11.4892 234.956 11.4333 234.809 11.4333H230.303C230.148 11.4333 230.017 11.4914 229.91 11.5967C229.802 11.7042 229.744 11.8354 229.744 11.9945C229.744 12.1536 229.802 12.2869 229.91 12.3922C230.017 12.4997 230.148 12.5578 230.303 12.5578H234.809C234.953 12.5578 235.093 12.4997 235.207 12.3922C235.315 12.2847 235.373 12.1536 235.373 11.9945C235.373 11.8354 235.315 11.7021 235.207 11.5967Z"
          fill="white"
        />
        <path
          d="M228.078 19.1346C228.186 19.0185 228.244 18.8809 228.244 18.7326V5.23946C228.244 5.08252 228.186 4.94707 228.078 4.83742C227.971 4.72992 227.84 4.67188 227.68 4.67188C227.521 4.67188 227.388 4.72992 227.281 4.83742C227.173 4.94492 227.117 5.08037 227.117 5.23946V18.7326C227.117 18.8766 227.173 19.0207 227.281 19.1346C227.388 19.2421 227.519 19.3002 227.68 19.3002C227.842 19.3002 227.971 19.2421 228.078 19.1346Z"
          fill="white"
        />
        <path
          d="M237.837 19.1346C237.942 19.0185 238 18.8809 238 18.7326V5.23946C238 5.08252 237.942 4.94707 237.837 4.83742C237.729 4.72992 237.598 4.67188 237.437 4.67188C237.276 4.67188 237.144 4.72992 237.039 4.83742C236.932 4.94492 236.874 5.08037 236.874 5.23946V18.7326C236.874 18.8766 236.932 19.0207 237.039 19.1346C237.144 19.2421 237.278 19.3002 237.437 19.3002C237.596 19.3002 237.729 19.2421 237.837 19.1346Z"
          fill="white"
        />
        <path
          d="M113.621 19.1411C113.514 19.2486 113.383 19.3045 113.226 19.3045C113.069 19.3045 112.937 19.2464 112.832 19.1411C112.725 19.0336 112.671 18.8981 112.671 18.7412V5.23946C112.671 4.96857 112.793 4.79227 113.045 4.70842C113.303 4.61812 113.514 4.68262 113.675 4.89332L122.999 17.1008V5.24806C122.999 5.09111 123.057 4.95567 123.165 4.85032C123.27 4.74282 123.403 4.68477 123.558 4.68477C123.713 4.68477 123.838 4.74282 123.954 4.85032C124.061 4.95782 124.117 5.09326 124.117 5.24806V18.7498C124.117 19.0164 123.99 19.1927 123.732 19.2808C123.679 19.3002 123.621 19.3088 123.558 19.3088C123.373 19.3088 123.225 19.2378 123.111 19.0916L113.785 6.88418V18.739C113.785 18.896 113.731 19.0314 113.625 19.1368L113.621 19.1411Z"
          fill="white"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M101.996 18.1821C100.304 18.1821 98.8572 17.578 97.6532 16.3697C96.4514 15.1572 95.8559 13.693 95.8559 11.9924C95.8559 10.2918 96.4557 8.82768 97.6532 7.61511C98.8615 6.40683 100.304 5.8027 101.996 5.8027C103.688 5.8027 105.137 6.40683 106.341 7.61511C107.541 8.82768 108.139 10.2918 108.139 11.9924C108.139 13.693 107.541 15.1572 106.341 16.3697C105.133 17.578 103.69 18.1821 101.996 18.1821ZM96.8642 6.81747C98.281 5.39205 99.9988 4.68042 102 4.68042C104.002 4.68042 105.713 5.39635 107.139 6.81747C108.553 8.24719 109.259 9.97361 109.259 11.9924C109.259 14.0112 108.553 15.7376 107.139 17.1674C105.72 18.5928 104.002 19.3044 102 19.3044C99.9988 19.3044 98.2896 18.5885 96.8642 17.1674C95.4495 15.7376 94.7422 14.0112 94.7422 11.9924C94.7422 9.97361 95.4495 8.24719 96.8642 6.81747Z"
          fill="white"
        />
        <path
          d="M89.6919 19.3044C89.8467 19.3044 89.98 19.2463 90.0853 19.141C90.1928 19.0335 90.2466 18.898 90.2466 18.7432V5.23936C90.2466 5.08241 90.1928 4.94697 90.0853 4.84162C89.98 4.73412 89.8467 4.67822 89.6919 4.67822C89.5371 4.67822 89.4038 4.73627 89.2963 4.84162C89.1888 4.94697 89.1372 5.08456 89.1372 5.23936V18.7411C89.1372 18.898 89.1888 19.0335 89.2963 19.1388C89.4038 19.2463 89.5349 19.3022 89.6919 19.3022"
          fill="white"
        />
        <path
          d="M58.7601 19.3043C58.9171 19.3043 59.0482 19.2463 59.1557 19.1388C59.2589 19.027 59.317 18.8915 59.317 18.7367V6.88404L68.6435 19.0915C68.7574 19.2355 68.9101 19.3086 69.0907 19.3086C69.2713 19.3086 69.2046 19.2957 69.2627 19.2807C69.5207 19.1947 69.6497 19.0141 69.6497 18.7496V5.24793C69.6497 5.09098 69.5959 4.95553 69.4906 4.85019C69.3745 4.74269 69.2519 4.68464 69.095 4.68464C68.938 4.68464 68.8069 4.74269 68.7015 4.85019C68.5983 4.95768 68.5403 5.09313 68.5403 5.24793V17.1028L59.2138 4.89534C59.0525 4.68464 58.844 4.62444 58.586 4.71044C58.3345 4.79214 58.2119 4.96843 58.2119 5.24148V18.7432C58.2119 18.9001 58.2657 19.0356 58.3732 19.1409C58.4807 19.2484 58.6118 19.3065 58.7666 19.3065H58.7623"
          fill="white"
        />
        <path
          d="M53.7316 18.7369C53.7316 18.5799 53.6778 18.4445 53.5725 18.3391C53.4586 18.2316 53.3253 18.1736 53.1791 18.1736H43.9644C43.8074 18.1736 43.6763 18.2316 43.5688 18.3391C43.4613 18.4466 43.4097 18.5821 43.4097 18.7369C43.4097 18.8917 43.4634 19.0293 43.5688 19.1346C43.6763 19.2421 43.8074 19.3002 43.9644 19.3002H53.1791C53.3274 19.3002 53.4586 19.2421 53.5725 19.1346C53.68 19.0271 53.7316 18.8917 53.7316 18.7369Z"
          fill="white"
        />
        <path
          d="M53.1724 4.67188H43.9577C43.8008 4.67188 43.6696 4.72992 43.5621 4.83527C43.4546 4.94062 43.4009 5.07822 43.4009 5.23301C43.4009 5.38781 43.4546 5.52541 43.5621 5.63076C43.6696 5.73825 43.8008 5.7963 43.9577 5.7963H53.1724C53.3208 5.7963 53.4519 5.73825 53.5659 5.63076C53.6734 5.52326 53.7271 5.38781 53.7271 5.23301C53.7271 5.07822 53.6734 4.94062 53.5659 4.83527C53.4519 4.72777 53.3186 4.67188 53.1724 4.67188Z"
          fill="white"
        />
        <path
          d="M53.1724 11.427H43.9577C43.8008 11.427 43.6696 11.4851 43.5621 11.5904C43.4546 11.6979 43.4009 11.8333 43.4009 11.9881C43.4009 12.1429 43.4546 12.2805 43.5621 12.3859C43.6696 12.4934 43.8008 12.5514 43.9577 12.5514H53.1724C53.3208 12.5514 53.4519 12.4934 53.5659 12.3859C53.6734 12.2784 53.7271 12.1429 53.7271 11.9881C53.7271 11.8333 53.6734 11.6957 53.5659 11.5904C53.4519 11.4829 53.3186 11.427 53.1724 11.427Z"
          fill="white"
        />
        <path
          d="M29.1299 5.79845H33.7415V18.7412C33.7415 18.8981 33.7953 19.0336 33.9006 19.1411C34.0081 19.2486 34.1393 19.3045 34.2962 19.3045C34.4532 19.3045 34.5843 19.2464 34.6918 19.1411C34.7993 19.0336 34.8509 18.8981 34.8509 18.7412V5.79845H39.4583C39.6066 5.79845 39.7378 5.7404 39.8539 5.63505C39.9614 5.52756 40.0151 5.39211 40.0151 5.23516C40.0151 5.07822 39.9614 4.94277 39.8539 4.83742C39.7399 4.72992 39.6066 4.67188 39.4583 4.67188H29.1299C28.9729 4.67188 28.8418 4.72992 28.7343 4.83742C28.6268 4.94492 28.5752 5.08037 28.5752 5.23516C28.5752 5.38996 28.6289 5.52756 28.7343 5.63505C28.8418 5.74255 28.9729 5.79845 29.1299 5.79845Z"
          fill="white"
        />
        <path
          d="M25.0618 18.395L20.1578 11.9903L25.0618 5.57483C25.1564 5.45228 25.193 5.31253 25.1736 5.16419C25.1521 5.00724 25.0833 4.88469 24.9608 4.78579C24.8382 4.6912 24.7028 4.65465 24.5544 4.674C24.4018 4.69335 24.2749 4.76859 24.1803 4.89114L19.4612 11.068L14.742 4.89114C14.6431 4.76859 14.5206 4.69335 14.3636 4.674C14.2153 4.6525 14.0756 4.6912 13.9487 4.78579C13.8305 4.88039 13.7638 5.00724 13.7466 5.16419C13.7273 5.31683 13.7595 5.45228 13.8498 5.57483L18.7539 11.9903L13.8498 18.395C13.7638 18.5176 13.7273 18.6573 13.7466 18.8142C13.7595 18.9712 13.824 19.0937 13.9487 19.1926C14.0519 19.2657 14.1078 19.3044 14.2927 19.3044C14.4776 19.3044 14.6259 19.2313 14.7399 19.0873L19.459 12.9105L24.1782 19.0873C24.29 19.2313 24.4405 19.3044 24.6232 19.3044C24.806 19.3044 24.8576 19.2679 24.9608 19.1926C25.0833 19.098 25.1543 18.9712 25.1736 18.8142C25.1951 18.6573 25.1521 18.5176 25.0618 18.395Z"
          fill="white"
        />
        <path
          d="M10.3241 18.7369C10.3241 18.5799 10.2703 18.4445 10.1628 18.3391C10.0467 18.2316 9.91559 18.1736 9.76725 18.1736H0.554688C0.397742 18.1736 0.266594 18.2316 0.161247 18.3391C0.0580488 18.4466 0 18.5821 0 18.7369C0 18.8917 0.0537489 19.0293 0.161247 19.1346C0.268744 19.2421 0.399892 19.3002 0.554688 19.3002H9.76939C9.91774 19.3002 10.0489 19.2421 10.165 19.1346C10.2682 19.0271 10.3262 18.8917 10.3262 18.7369"
          fill="white"
        />
        <path
          d="M9.76939 4.67188H0.554688C0.397742 4.67188 0.266594 4.72992 0.161247 4.83527C0.0580488 4.94277 0 5.07822 0 5.23301C0 5.38781 0.0537489 5.52541 0.161247 5.63076C0.268744 5.73825 0.399892 5.7963 0.554688 5.7963H9.76939C9.91774 5.7963 10.0489 5.73825 10.165 5.63076C10.2682 5.52326 10.3262 5.38781 10.3262 5.23301C10.3262 5.07822 10.2725 4.94062 10.165 4.83527C10.0489 4.72777 9.91774 4.67188 9.76939 4.67188Z"
          fill="white"
        />
        <path
          d="M9.76939 11.427H0.554688C0.397742 11.427 0.266594 11.4851 0.161247 11.5904C0.0580488 11.6979 0 11.8333 0 11.9881C0 12.1429 0.0537489 12.2805 0.161247 12.3859C0.268744 12.4934 0.399892 12.5514 0.554688 12.5514H9.76939C9.91774 12.5514 10.0489 12.4934 10.165 12.3859C10.2682 12.2784 10.3262 12.1429 10.3262 11.9881C10.3262 11.8333 10.2725 11.6957 10.165 11.5904C10.0489 11.4829 9.91774 11.427 9.76939 11.427Z"
          fill="white"
        />
        <path
          d="M80.7006 18.167H78.0583C77.2908 18.167 76.6393 17.8918 76.1018 17.3436C75.5579 16.8018 75.2849 16.1439 75.2849 15.3699C75.2849 15.213 75.2311 15.0819 75.1236 14.9744C75.0161 14.8647 74.885 14.811 74.7302 14.811C74.5754 14.811 74.4442 14.8647 74.3367 14.9744C74.2292 15.0819 74.1755 15.2152 74.1755 15.3699C74.1755 16.4471 74.556 17.3694 75.315 18.1369C76.0739 18.9023 76.9898 19.2872 78.0583 19.2872H80.7006C81.7777 19.2872 82.6915 18.9045 83.4461 18.1369C84.205 17.3716 84.5856 16.4492 84.5856 15.3699C84.5856 14.2907 84.205 13.3705 83.4461 12.603C82.6915 11.8376 80.4555 11.4549 79.3805 11.4549H79.1075C78.34 11.4549 76.6393 11.1797 76.0997 10.6314C75.5558 10.0897 75.2827 9.43177 75.2827 8.65779C75.2827 7.88381 75.5558 7.22377 76.0997 6.67553C76.6372 6.13374 77.2908 5.8607 78.0562 5.8607H80.6985C81.466 5.8607 82.1217 6.13159 82.6657 6.67553C83.2053 7.22377 83.1623 7.57421 83.1623 8.3482C83.1623 8.50514 83.216 8.63629 83.3235 8.74379C83.4375 8.85344 83.5686 8.90719 83.717 8.90719C83.8653 8.90719 84.0029 8.85344 84.1104 8.74379C84.2179 8.63629 84.2717 8.50299 84.2717 8.3482C84.2717 7.27107 84.2029 6.65833 83.4439 5.89295C82.6893 5.12757 81.7756 4.74487 80.6985 4.74487H78.0562C76.9876 4.74487 76.0717 5.12757 75.3128 5.89295C74.5517 6.65833 74.1733 7.58066 74.1733 8.65994C74.1733 9.73922 74.5539 10.6594 75.3128 11.4269C76.0717 12.1923 78.0368 12.575 79.1075 12.575H79.3805C80.1459 12.575 82.1239 12.8502 82.6678 13.3984C83.2075 13.9402 83.4762 14.5981 83.4762 15.3721C83.4762 16.1461 83.2075 16.8018 82.6678 17.3458C82.1239 17.894 81.4681 18.1692 80.7006 18.1692"
          fill="white"
        />
        <path
          d="M134.363 11.5797C134.243 11.4658 134.099 11.4077 133.944 11.4077H129.212C129.047 11.4077 128.909 11.4658 128.795 11.5797C128.681 11.6937 128.623 11.8313 128.623 11.999C128.623 12.1666 128.681 12.3021 128.795 12.4182C128.909 12.5321 129.047 12.5902 129.212 12.5902H133.944C134.099 12.5902 134.243 12.5321 134.363 12.4182C134.477 12.3042 134.533 12.1666 134.533 11.999C134.533 11.8313 134.477 11.6937 134.363 11.5797Z"
          fill="white"
        />
        <path
          d="M151.699 11.5797C151.578 11.4658 151.434 11.4077 151.28 11.4077H146.55C146.382 11.4077 146.244 11.4658 146.13 11.5797C146.017 11.6937 145.958 11.8313 145.958 11.999C145.958 12.1666 146.017 12.3021 146.13 12.4182C146.244 12.5321 146.382 12.5902 146.55 12.5902H151.28C151.434 12.5902 151.578 12.5321 151.699 12.4182C151.813 12.3042 151.871 12.1666 151.871 11.999C151.871 11.8313 151.813 11.6937 151.699 11.5797Z"
          fill="white"
        />
        <path
          d="M140.661 6.11221C140.777 5.99181 140.833 5.84991 140.833 5.69511V0.963064C140.833 0.797517 140.777 0.65992 140.661 0.543823C140.547 0.429875 140.409 0.371826 140.244 0.371826C140.078 0.371826 139.938 0.429875 139.824 0.543823C139.708 0.65777 139.652 0.795367 139.652 0.963064V5.69511C139.652 5.84991 139.708 5.99396 139.824 6.11221C139.938 6.22615 140.076 6.2842 140.244 6.2842C140.411 6.2842 140.547 6.22615 140.661 6.11221Z"
          fill="white"
        />
        <path
          d="M140.661 23.4538C140.777 23.3334 140.833 23.1893 140.833 23.0367V18.3046C140.833 18.1391 140.777 18.0015 140.661 17.8854C140.547 17.7714 140.409 17.7134 140.244 17.7134C140.078 17.7134 139.938 17.7714 139.824 17.8854C139.708 17.9993 139.652 18.1369 139.652 18.3046V23.0367C139.652 23.1915 139.708 23.3355 139.824 23.4538C139.938 23.5677 140.076 23.6258 140.244 23.6258C140.411 23.6258 140.547 23.5677 140.661 23.4538Z"
          fill="white"
        />
        <path
          d="M143.552 7.11194C143.714 7.06464 143.834 6.97004 143.911 6.83674L146.276 2.73893C146.36 2.59488 146.38 2.44653 146.337 2.29174C146.296 2.13479 146.203 2.01654 146.059 1.93269C145.915 1.84885 145.769 1.8295 145.612 1.8725C145.455 1.91549 145.337 2.00364 145.253 2.14769L142.888 6.2455C142.811 6.3788 142.789 6.53145 142.828 6.69269C142.871 6.84964 142.961 6.96789 143.105 7.05174C143.249 7.13558 143.398 7.15493 143.552 7.11194Z"
          fill="white"
        />
        <path
          d="M134.886 22.1273C135.045 22.08 135.168 21.9854 135.245 21.8521L137.61 17.7543C137.692 17.6102 137.711 17.4619 137.67 17.3049C137.627 17.148 137.537 17.0297 137.393 16.9459C137.249 16.862 137.1 16.8427 136.943 16.8857C136.787 16.9265 136.668 17.0168 136.587 17.1609L134.219 21.2587C134.144 21.392 134.121 21.5446 134.161 21.7059C134.202 21.8628 134.293 21.9811 134.437 22.0649C134.581 22.1488 134.729 22.1681 134.886 22.1251"
          fill="white"
        />
        <path
          d="M145.554 9.41902C145.715 9.45772 145.87 9.43622 146.003 9.35882L150.101 6.99387C150.245 6.91002 150.335 6.79177 150.376 6.63482C150.417 6.47788 150.398 6.32953 150.316 6.18548C150.232 6.04144 150.114 5.95114 149.957 5.90814C149.8 5.86514 149.654 5.88449 149.51 5.96834L145.412 8.33329C145.279 8.41069 145.182 8.53108 145.137 8.69018C145.094 8.84713 145.113 8.99548 145.197 9.13952C145.279 9.28357 145.399 9.37387 145.554 9.41687"
          fill="white"
        />
        <path
          d="M130.543 18.0897C130.705 18.1284 130.857 18.1069 130.993 18.0295L135.088 15.6645C135.232 15.5807 135.323 15.4624 135.366 15.3055C135.407 15.1486 135.387 15.0002 135.306 14.8562C135.222 14.7121 135.103 14.6218 134.946 14.5788C134.79 14.538 134.643 14.5552 134.499 14.639L130.401 17.004C130.268 17.0814 130.171 17.2018 130.124 17.363C130.083 17.52 130.103 17.6683 130.186 17.8123C130.268 17.9564 130.386 18.0467 130.543 18.0897Z"
          fill="white"
        />
        <path
          d="M145.137 15.3056C145.185 15.4647 145.279 15.5872 145.415 15.6646L149.51 18.0296C149.654 18.1134 149.803 18.1328 149.96 18.0898C150.117 18.0468 150.235 17.9565 150.317 17.8124C150.4 17.6684 150.42 17.52 150.379 17.3631C150.336 17.2062 150.246 17.0879 150.102 17.0062L146.004 14.6413C145.871 14.5639 145.718 14.5424 145.557 14.5811C145.4 14.6241 145.281 14.7144 145.198 14.8584C145.116 15.0025 145.097 15.1508 145.137 15.3077"
          fill="white"
        />
        <path
          d="M130.124 6.6347C130.169 6.79379 130.266 6.91634 130.399 6.99374L134.497 9.35869C134.641 9.44254 134.787 9.46189 134.944 9.41889C135.101 9.37589 135.219 9.28559 135.303 9.14154C135.385 8.9975 135.404 8.84915 135.363 8.6922C135.32 8.53526 135.23 8.41701 135.088 8.33531L130.99 5.97036C130.855 5.89296 130.702 5.87146 130.541 5.91016C130.386 5.95316 130.266 6.04346 130.184 6.18751C130.1 6.33155 130.081 6.4799 130.124 6.63685"
          fill="white"
        />
        <path
          d="M142.83 17.3049C142.791 17.4662 142.813 17.6188 142.89 17.7521L145.255 21.8499C145.339 21.994 145.457 22.0843 145.614 22.1251C145.769 22.166 145.917 22.1466 146.061 22.0649C146.205 21.9811 146.296 21.8628 146.339 21.7059C146.382 21.5489 146.362 21.4006 146.278 21.2587L143.913 17.1609C143.836 17.0276 143.713 16.9308 143.554 16.8857C143.397 16.8448 143.251 16.8642 143.107 16.9459C142.963 17.0297 142.871 17.148 142.83 17.3049Z"
          fill="white"
        />
        <path
          d="M134.157 2.28978C134.118 2.45103 134.14 2.60367 134.217 2.73697L136.584 6.83478C136.666 6.97883 136.784 7.06913 136.941 7.10998C137.098 7.15083 137.246 7.13148 137.391 7.04978C137.535 6.96593 137.625 6.84768 137.668 6.69074C137.709 6.53379 137.689 6.38759 137.608 6.24355L135.241 2.14573C135.163 2.01244 135.043 1.91569 134.884 1.87054C134.727 1.82754 134.578 1.84904 134.434 1.93074C134.29 2.01459 134.2 2.13283 134.157 2.28978Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_418_1423">
          <rect
            width="238"
            height="23.2518"
            fill="white"
            transform="translate(0 0.374023)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const BoutiqueCircle = ({ className }: { className?: string }) => {
  return (
    <svg
      width="124"
      height="124"
      viewBox="0 0 124 124"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M90.8667 74.6416C89.7386 75.6067 88.162 76.3112 87.7977 77.4172C87.3212 78.8966 87.174 80.6014 89.3392 81.799C96.4372 85.7229 103.388 89.9216 110.325 94.1202C112.938 95.6983 114.977 94.8952 116.057 92.5352C117.052 90.3584 115.685 88.724 113.604 87.5546C107.732 84.2435 101.959 80.7564 96.1219 77.375C94.5804 76.4803 92.9548 75.7194 90.8597 74.6416H90.8667Z"
        fill="black"
      />
      <path
        d="M90.7581 48.9116C92.0544 48.3903 92.8181 48.186 93.4838 47.8056C100.302 43.8535 107.189 40.0001 113.846 35.7732C115.1 34.9772 116.333 32.3707 115.934 31.1308C115.233 28.9469 113.131 27.9254 110.609 29.4401C103.546 33.6669 96.3706 37.7035 89.4128 42.0924C88.3617 42.7546 87.682 44.9666 87.8502 46.3333C87.9693 47.3195 89.7771 48.0945 90.7651 48.9116H90.7581Z"
        fill="black"
      />
      <path
        d="M36.4904 44.0802C36.2522 43.7844 35.7197 42.622 34.8228 42.0795C27.6687 37.7893 20.4096 33.6752 13.2625 29.3779C10.747 27.8633 8.83416 29.1595 8.21054 31.0264C7.79714 32.2733 8.92524 34.9221 10.1515 35.7041C16.8921 40.0084 23.857 43.9534 30.7798 47.9619C33.6316 49.6103 36.4064 48.1521 36.4975 44.0732L36.4904 44.0802Z"
        fill="black"
      />
      <path
        d="M37.0881 78.8416C36.2543 77.5312 35.8058 75.9532 34.923 75.6433C33.6687 75.2065 31.8469 75.2488 30.7048 75.8828C23.817 79.7081 17.0694 83.8081 10.2306 87.7249C8.11452 88.9366 7.02143 90.6274 8.17758 92.7901C9.22861 94.7485 11.0154 95.6855 13.4047 94.2624C20.5658 89.9793 27.811 85.837 34.965 81.5467C35.7848 81.0536 36.2473 79.9476 37.0881 78.8416Z"
        fill="black"
      />
      <path
        d="M95.0803 112.911C94.5548 111.615 94.3376 110.854 93.9593 110.184C90.0774 103.407 86.1746 96.6516 82.2717 89.8886C81.1576 87.9654 79.9524 86.4156 77.3669 87.8316C74.9355 89.163 74.5291 90.9453 75.8604 93.2419C79.9594 100.294 83.9744 107.395 88.2977 114.299C88.9633 115.362 91.1635 116.039 92.5298 115.87C93.5108 115.75 94.2746 113.918 95.0874 112.911H95.0803Z"
        fill="black"
      />
      <path
        d="M33.8539 116.119C34.1482 115.872 35.1992 115.294 35.8298 114.407C37.1471 112.533 38.2893 110.532 39.4244 108.531C42.4303 103.248 45.4643 97.9855 48.3372 92.6316C49.4863 90.49 48.8347 88.7077 46.4733 87.6862C44.3082 86.7492 43.054 87.7566 42.0239 89.5389C38.002 96.4921 33.931 103.417 29.937 110.391C28.2974 113.258 29.7829 116.048 33.8539 116.119Z"
        fill="black"
      />
      <path
        d="M33.8511 7.5754C29.6539 7.64585 28.3787 10.499 30.1514 13.5705C33.9281 20.1291 37.6628 26.7159 41.4536 33.2675C42.6237 35.2823 43.7378 37.445 46.7718 35.8388C49.8549 34.2115 49.007 31.9994 47.7528 29.8085C43.8289 22.9329 39.87 16.0784 35.813 9.28023C35.2665 8.36441 34.1314 7.81492 33.8441 7.56836L33.8511 7.5754Z"
        fill="black"
      />
      <path
        d="M90.3321 7.58203C90.0449 7.78633 88.8607 8.18083 88.3772 9.00506C84.068 16.3245 79.8989 23.7285 75.6457 31.0832C74.2863 33.4291 75.5335 34.676 77.3483 35.8454C79.4714 37.2121 80.9709 36.1061 81.9589 34.4435C86.114 27.434 90.164 20.3611 94.1859 13.2742C95.8186 10.3999 94.284 7.6243 90.3321 7.58908V7.58203Z"
        fill="black"
      />
      <path
        d="M15.8069 58.3154C11.7359 58.3154 7.65786 58.3506 3.58685 58.3013C1.1134 58.2731 -0.00069973 59.4003 0.0133141 61.8941C0.0273279 64.4302 1.26755 65.4024 3.69195 65.3883C11.841 65.346 19.99 65.3601 28.1391 65.3742C30.5705 65.3742 32.2521 64.3527 32.2101 61.7955C32.168 59.2453 30.4444 58.2872 28.034 58.3083C23.963 58.3436 19.8849 58.3154 15.8139 58.3154H15.8069Z"
        fill="black"
      />
      <path
        d="M108.101 65.3656C111.934 65.3656 115.774 65.3304 119.607 65.3797C122.122 65.4079 124.035 64.5625 123.993 61.7728C123.958 59.2297 122.22 58.2927 119.81 58.3068C111.906 58.335 104.002 58.342 96.0983 58.3068C93.6949 58.2927 91.9292 59.1874 91.8872 61.7446C91.8381 64.5132 93.709 65.4149 96.2385 65.3867C100.19 65.3374 104.142 65.3727 108.094 65.3727L108.101 65.3656Z"
        fill="black"
      />
      <path
        d="M65.2035 15.6046C65.2035 11.258 65.3156 6.91141 65.1194 2.57187C65.0774 1.63492 63.8792 -0.034675 63.2906 0.000548537C61.8122 0.0780404 60.0885 0.627528 59.0094 1.5997C58.2667 2.26895 58.2386 3.95968 58.2246 5.1925C58.1545 12.2372 58.1896 19.2819 58.1966 26.3266C58.1966 27.0241 58.2176 27.7426 58.3788 28.4189C58.8833 30.4901 59.7101 32.054 62.3727 31.582C64.7761 31.1523 65.2596 29.687 65.2245 27.5806C65.1615 23.5863 65.2035 19.5989 65.2035 15.6046Z"
        fill="black"
      />
      <path
        d="M65.2078 108.337C65.2078 104.11 65.1728 99.8834 65.2218 95.6566C65.2568 92.8528 63.7363 91.9299 61.2279 92.1413C58.0117 92.409 58.313 95.1141 58.2639 97.0937C58.0958 104.371 58.1728 111.655 58.2289 118.932C58.2359 119.933 58.7124 120.926 58.8736 121.933C59.322 124.688 61.5362 123.976 63.0006 123.695C63.8765 123.525 65.0536 121.99 65.1097 121.01C65.3409 116.798 65.2078 112.564 65.2078 108.33V108.337Z"
        fill="black"
      />
      <path
        d="M90.8597 74.6416C92.9548 75.7265 94.5804 76.4803 96.1219 77.375C101.959 80.7564 107.732 84.2365 113.604 87.5546C115.685 88.731 117.052 90.3654 116.057 92.5352C114.978 94.8881 112.938 95.6983 110.325 94.1202C103.381 89.9216 96.4372 85.7229 89.3392 81.799C87.1741 80.6014 87.3212 78.8896 87.7977 77.4172C88.155 76.3112 89.7386 75.5997 90.8667 74.6416H90.8597Z"
        fill="black"
      />
      <path
        d="M90.7565 48.9101C89.7686 48.0929 87.9608 47.318 87.8417 46.3317C87.6805 44.958 88.3532 42.753 89.4042 42.0908C96.3621 37.702 103.537 33.6654 110.6 29.4385C113.13 27.9239 115.225 28.9524 115.925 31.1293C116.325 32.3691 115.085 34.9757 113.837 35.7717C107.181 39.9986 100.3 43.845 93.4752 47.8041C92.8096 48.1916 92.0458 48.3888 90.7495 48.9101H90.7565Z"
        fill="black"
      />
      <path
        d="M36.4906 44.08C36.3995 48.1519 33.6248 49.6172 30.773 47.9687C23.8501 43.9603 16.8853 40.0082 10.1446 35.7109C8.91841 34.9289 7.7903 32.2801 8.20371 31.0332C8.82732 29.1664 10.7402 27.8701 13.2557 29.3847C20.4028 33.682 27.6619 37.7961 34.816 42.0864C35.7199 42.6288 36.2454 43.7912 36.4836 44.0871L36.4906 44.08Z"
        fill="black"
      />
      <path
        d="M37.0843 78.8416C36.2435 79.9476 35.781 81.0536 34.9612 81.5467C27.8072 85.844 20.562 89.9863 13.4009 94.2624C11.0116 95.6855 9.23183 94.7485 8.17379 92.7901C7.01064 90.6274 8.11073 88.9366 10.2268 87.7249C17.0656 83.8081 23.8132 79.7151 30.701 75.8828C31.8432 75.2488 33.672 75.2065 34.9192 75.6433C35.8021 75.9462 36.2505 77.5242 37.0843 78.8416Z"
        fill="black"
      />
      <path
        d="M95.0805 112.911C94.2677 113.918 93.4969 115.75 92.523 115.87C91.1566 116.039 88.9565 115.362 88.2908 114.299C83.9676 107.395 79.9526 100.294 75.8535 93.2419C74.5222 90.9453 74.9286 89.163 77.36 87.8316C79.9456 86.4156 81.1508 87.9654 82.2649 89.8886C86.1677 96.6516 90.0706 103.407 93.9524 110.184C94.3378 110.854 94.548 111.622 95.0735 112.911H95.0805Z"
        fill="black"
      />
      <path
        d="M33.8569 116.119C29.7859 116.048 28.2934 113.266 29.94 110.391C33.934 103.417 38.005 96.4921 42.027 89.5389C43.057 87.7566 44.3112 86.7492 46.4764 87.6862C48.8377 88.7077 49.4893 90.49 48.3402 92.6316C45.4673 97.9855 42.4334 103.255 39.4274 108.531C38.2923 110.525 37.1502 112.533 35.8329 114.407C35.2092 115.294 34.1512 115.872 33.8569 116.119Z"
        fill="black"
      />
      <path
        d="M33.8522 7.5752C34.1394 7.81472 35.2816 8.37125 35.8211 9.28706C39.8711 16.0852 43.837 22.9397 47.7609 29.8154C49.0081 32.0063 49.856 34.2183 46.7799 35.8456C43.7459 37.4448 42.6318 35.2891 41.4617 33.2743C37.6709 26.7227 33.9363 20.1359 30.1595 13.5773C28.3868 10.4988 29.655 7.65269 33.8592 7.58224L33.8522 7.5752Z"
        fill="black"
      />
      <path
        d="M90.3422 7.58224C94.2941 7.61746 95.8217 10.3931 94.196 13.2673C90.1741 20.3543 86.1311 27.4272 81.969 34.4367C80.981 36.0992 79.4815 37.2053 77.3584 35.8386C75.5436 34.6692 74.3034 33.4223 75.6558 31.0764C79.909 23.7217 84.0781 16.3177 88.3873 8.99823C88.8708 8.18104 90.055 7.77949 90.3422 7.5752V7.58224Z"
        fill="black"
      />
      <path
        d="M15.8007 58.3154C19.8717 58.3154 23.9498 58.3365 28.0208 58.3083C30.4312 58.2872 32.1549 59.2453 32.1969 61.7955C32.2389 64.3527 30.5573 65.3812 28.1259 65.3742C19.9768 65.3601 11.8278 65.339 3.67876 65.3883C1.25437 65.4024 0.0141443 64.4372 0.000130509 61.8941C-0.0138833 59.4003 1.10021 58.2731 3.57365 58.3013C7.64467 58.3506 11.7227 58.3154 15.7937 58.3154H15.8007Z"
        fill="black"
      />
      <path
        d="M108.101 65.3667C104.149 65.3667 100.197 65.3314 96.2453 65.3808C93.7088 65.4089 91.845 64.5072 91.894 61.7386C91.9361 59.1814 93.7018 58.2938 96.1052 58.3008C104.009 58.336 111.913 58.336 119.817 58.3008C122.227 58.2938 123.965 59.2237 124 61.7668C124.035 64.5565 122.129 65.4019 119.613 65.3737C115.781 65.3244 111.941 65.3596 108.108 65.3596L108.101 65.3667Z"
        fill="black"
      />
      <path
        d="M65.2061 15.6046C65.2061 19.5989 65.157 23.5933 65.2271 27.5806C65.2621 29.687 64.7857 31.1593 62.3753 31.582C59.7127 32.054 58.8858 30.4901 58.3813 28.4189C58.2202 27.7497 58.1992 27.0311 58.1992 26.3266C58.1851 19.2819 58.1571 12.2372 58.2272 5.1925C58.2412 3.95263 58.2692 2.2619 59.012 1.5997C60.091 0.627528 61.8147 0.0850851 63.2932 0.000548537C63.8818 -0.034675 65.08 1.63492 65.122 2.57187C65.3182 6.90437 65.2061 11.258 65.2131 15.6046H65.2061Z"
        fill="black"
      />
      <path
        d="M65.2078 108.337C65.2078 112.564 65.3409 116.798 65.1097 121.017C65.0536 121.997 63.8765 123.532 63.0006 123.702C61.5362 123.99 59.322 124.695 58.8736 121.94C58.7124 120.926 58.2359 119.94 58.2289 118.939C58.1728 111.662 58.0958 104.378 58.2639 97.1007C58.313 95.1141 58.0117 92.416 61.2279 92.1483C63.7363 91.9369 65.2568 92.8527 65.2218 95.6636C65.1728 99.8904 65.2078 104.117 65.2078 108.344V108.337Z"
        fill="black"
      />
    </svg>
  );
};

export const SpinningBoutiqueCircle = ({
  className,
}: {
  className?: string;
}) => {
  return (
    // biome-ignore lint/a11y/noSvgWithoutTitle: <explanation>
    <motion.svg
      width="124"
      height="124"
      viewBox="0 0 124 124"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      animate={{
        rotate: 90,
      }}
      className={className}
      transition={{
        duration: 2,
        ease: "easeOut",
      }}
    >
      <path
        d="M90.8667 74.6416C89.7386 75.6067 88.162 76.3112 87.7977 77.4172C87.3212 78.8966 87.174 80.6014 89.3392 81.799C96.4372 85.7229 103.388 89.9216 110.325 94.1202C112.938 95.6983 114.977 94.8952 116.057 92.5352C117.052 90.3584 115.685 88.724 113.604 87.5546C107.732 84.2435 101.959 80.7564 96.1219 77.375C94.5804 76.4803 92.9548 75.7194 90.8597 74.6416H90.8667Z"
        fill="black"
      />
      <path
        d="M90.7581 48.9116C92.0544 48.3903 92.8181 48.186 93.4838 47.8056C100.302 43.8535 107.189 40.0001 113.846 35.7732C115.1 34.9772 116.333 32.3707 115.934 31.1308C115.233 28.9469 113.131 27.9254 110.609 29.4401C103.546 33.6669 96.3706 37.7035 89.4128 42.0924C88.3617 42.7546 87.682 44.9666 87.8502 46.3333C87.9693 47.3195 89.7771 48.0945 90.7651 48.9116H90.7581Z"
        fill="black"
      />
      <path
        d="M36.4904 44.0802C36.2522 43.7844 35.7197 42.622 34.8228 42.0795C27.6687 37.7893 20.4096 33.6752 13.2625 29.3779C10.747 27.8633 8.83416 29.1595 8.21054 31.0264C7.79714 32.2733 8.92524 34.9221 10.1515 35.7041C16.8921 40.0084 23.857 43.9534 30.7798 47.9619C33.6316 49.6103 36.4064 48.1521 36.4975 44.0732L36.4904 44.0802Z"
        fill="black"
      />
      <path
        d="M37.0881 78.8416C36.2543 77.5312 35.8058 75.9532 34.923 75.6433C33.6687 75.2065 31.8469 75.2488 30.7048 75.8828C23.817 79.7081 17.0694 83.8081 10.2306 87.7249C8.11452 88.9366 7.02143 90.6274 8.17758 92.7901C9.22861 94.7485 11.0154 95.6855 13.4047 94.2624C20.5658 89.9793 27.811 85.837 34.965 81.5467C35.7848 81.0536 36.2473 79.9476 37.0881 78.8416Z"
        fill="black"
      />
      <path
        d="M95.0803 112.911C94.5548 111.615 94.3376 110.854 93.9593 110.184C90.0774 103.407 86.1746 96.6516 82.2717 89.8886C81.1576 87.9654 79.9524 86.4156 77.3669 87.8316C74.9355 89.163 74.5291 90.9453 75.8604 93.2419C79.9594 100.294 83.9744 107.395 88.2977 114.299C88.9633 115.362 91.1635 116.039 92.5298 115.87C93.5108 115.75 94.2746 113.918 95.0874 112.911H95.0803Z"
        fill="black"
      />
      <path
        d="M33.8539 116.119C34.1482 115.872 35.1992 115.294 35.8298 114.407C37.1471 112.533 38.2893 110.532 39.4244 108.531C42.4303 103.248 45.4643 97.9855 48.3372 92.6316C49.4863 90.49 48.8347 88.7077 46.4733 87.6862C44.3082 86.7492 43.054 87.7566 42.0239 89.5389C38.002 96.4921 33.931 103.417 29.937 110.391C28.2974 113.258 29.7829 116.048 33.8539 116.119Z"
        fill="black"
      />
      <path
        d="M33.8511 7.5754C29.6539 7.64585 28.3787 10.499 30.1514 13.5705C33.9281 20.1291 37.6628 26.7159 41.4536 33.2675C42.6237 35.2823 43.7378 37.445 46.7718 35.8388C49.8549 34.2115 49.007 31.9994 47.7528 29.8085C43.8289 22.9329 39.87 16.0784 35.813 9.28023C35.2665 8.36441 34.1314 7.81492 33.8441 7.56836L33.8511 7.5754Z"
        fill="black"
      />
      <path
        d="M90.3321 7.58203C90.0449 7.78633 88.8607 8.18083 88.3772 9.00506C84.068 16.3245 79.8989 23.7285 75.6457 31.0832C74.2863 33.4291 75.5335 34.676 77.3483 35.8454C79.4714 37.2121 80.9709 36.1061 81.9589 34.4435C86.114 27.434 90.164 20.3611 94.1859 13.2742C95.8186 10.3999 94.284 7.6243 90.3321 7.58908V7.58203Z"
        fill="black"
      />
      <path
        d="M15.8069 58.3154C11.7359 58.3154 7.65786 58.3506 3.58685 58.3013C1.1134 58.2731 -0.00069973 59.4003 0.0133141 61.8941C0.0273279 64.4302 1.26755 65.4024 3.69195 65.3883C11.841 65.346 19.99 65.3601 28.1391 65.3742C30.5705 65.3742 32.2521 64.3527 32.2101 61.7955C32.168 59.2453 30.4444 58.2872 28.034 58.3083C23.963 58.3436 19.8849 58.3154 15.8139 58.3154H15.8069Z"
        fill="black"
      />
      <path
        d="M108.101 65.3656C111.934 65.3656 115.774 65.3304 119.607 65.3797C122.122 65.4079 124.035 64.5625 123.993 61.7728C123.958 59.2297 122.22 58.2927 119.81 58.3068C111.906 58.335 104.002 58.342 96.0983 58.3068C93.6949 58.2927 91.9292 59.1874 91.8872 61.7446C91.8381 64.5132 93.709 65.4149 96.2385 65.3867C100.19 65.3374 104.142 65.3727 108.094 65.3727L108.101 65.3656Z"
        fill="black"
      />
      <path
        d="M65.2035 15.6046C65.2035 11.258 65.3156 6.91141 65.1194 2.57187C65.0774 1.63492 63.8792 -0.034675 63.2906 0.000548537C61.8122 0.0780404 60.0885 0.627528 59.0094 1.5997C58.2667 2.26895 58.2386 3.95968 58.2246 5.1925C58.1545 12.2372 58.1896 19.2819 58.1966 26.3266C58.1966 27.0241 58.2176 27.7426 58.3788 28.4189C58.8833 30.4901 59.7101 32.054 62.3727 31.582C64.7761 31.1523 65.2596 29.687 65.2245 27.5806C65.1615 23.5863 65.2035 19.5989 65.2035 15.6046Z"
        fill="black"
      />
      <path
        d="M65.2078 108.337C65.2078 104.11 65.1728 99.8834 65.2218 95.6566C65.2568 92.8528 63.7363 91.9299 61.2279 92.1413C58.0117 92.409 58.313 95.1141 58.2639 97.0937C58.0958 104.371 58.1728 111.655 58.2289 118.932C58.2359 119.933 58.7124 120.926 58.8736 121.933C59.322 124.688 61.5362 123.976 63.0006 123.695C63.8765 123.525 65.0536 121.99 65.1097 121.01C65.3409 116.798 65.2078 112.564 65.2078 108.33V108.337Z"
        fill="black"
      />
    </motion.svg>
  );
};

const body = [
  "M19.1897 48.3893C20.616 46.963 22.9281 46.963 24.3579 48.3893C25.7842 49.8156 25.7842 52.1277 24.3579 53.5575L7.20377 70.7117C5.77745 72.138 3.46536 72.138 2.03556 70.7117C0.609241 69.2854 0.609241 66.9698 2.03556 65.5435L19.1897 48.3893Z",
  "M44.2201 53.5858C42.7938 52.1595 42.7938 49.8474 44.2201 48.4176C45.6464 46.9913 47.9585 46.9913 49.3883 48.4176L66.5425 65.5718C67.9688 66.9981 67.9688 69.3102 66.5425 70.74C65.1162 72.1663 62.8041 72.1663 61.3743 70.74L44.2201 53.5858Z",
  "M1.74454 6.49932C0.318226 5.07301 0.318226 2.76091 1.74454 1.3346C3.17086 -0.0917168 5.48295 -0.0917168 6.91276 1.3346L24.0669 18.4887C25.4932 19.9151 25.4932 22.2272 24.0669 23.6535C22.6406 25.0798 20.3285 25.0798 18.8987 23.6535L1.74454 6.49932Z",
  "M61.5764 1.06974C63.0027 -0.356579 65.3148 -0.356579 66.7446 1.06974C68.171 2.49605 68.171 4.80815 66.7446 6.23795L49.5905 23.3921C48.1642 24.8184 45.8521 24.8184 44.4223 23.3921C42.996 21.9658 42.996 19.6537 44.4223 18.2239L61.5764 1.06974Z",
];
const brain = [
  "M32.4102 2.8949C32.4102 1.29705 33.7072 0 35.3051 0C36.9029 0 38.1972 1.29705 38.1972 2.8949V22.1105C38.1972 23.7084 36.9029 25.0054 35.3051 25.0054C33.7072 25.0054 32.4102 23.7084 32.4102 22.1105V2.8949Z",
  "M2.89216 38.895C1.29429 38.895 0 37.5979 0 36.0001C0 34.4022 1.29429 33.1052 2.89216 33.1052H22.1079C23.7058 33.1052 25.0029 34.4022 25.0029 36.0001C25.0029 37.5979 23.7058 38.895 22.1079 38.895H2.89216Z",
  "M49.8891 38.895C48.2912 38.895 46.9941 37.5979 46.9941 36.0001C46.9941 34.4022 48.2912 33.1052 49.8891 33.1052H69.1048C70.7027 33.1052 71.9998 34.4022 71.9998 36.0001C71.9998 37.5979 70.7027 38.895 69.1048 38.895H49.8891Z",
  "M32.4102 49.8894C32.4102 48.2915 33.7072 46.9945 35.3051 46.9945C36.9029 46.9945 38.1972 48.2888 38.1972 49.8894V69.105C38.1972 70.7028 36.9029 71.9999 35.3051 71.9999C33.7072 71.9999 32.4102 70.7028 32.4102 69.105V49.8894Z",
];
const resilience = [
  "M44.3377 70.9676C43.0289 72.3399 40.9069 72.3399 39.5949 70.9676C38.2861 69.5984 38.2861 67.3747 39.5949 66.0024L63.3121 41.1828C64.6241 39.8105 66.7461 39.8105 68.0581 41.1828C69.3669 42.5552 69.3669 44.7757 68.0581 46.148L44.3377 70.9676Z",
  "M39.5562 5.99443C38.2443 4.6221 38.2443 2.40158 39.5562 1.02925C40.865 -0.343083 42.9871 -0.343083 44.2991 1.02925L68.0163 25.8488C69.3251 27.2211 69.3251 29.4416 68.0163 30.814C66.7075 32.1863 64.5823 32.1863 63.2735 30.814L39.5562 5.99443Z",
  "M29.446 66.0058C30.7548 67.375 30.7548 69.5987 29.446 70.971C28.1372 72.3433 26.012 72.3433 24.7032 70.971L0.985936 46.1514C-0.326041 44.7791 -0.326041 42.5586 0.985936 41.1863C2.29474 39.8139 4.41994 39.8139 5.72874 41.1863L29.446 66.0058Z",
  "M24.7032 1.02925C26.012 -0.343083 28.1372 -0.343083 29.446 1.02925C30.7548 2.40158 30.7548 4.6221 29.446 5.99443L5.72874 30.814C4.41994 32.1863 2.29474 32.1863 0.985936 30.814C-0.326041 29.4416 -0.326041 27.2211 0.985936 25.8488L24.7032 1.02925Z",
];

export const BoutiqueControlledAnimation = ({
  style,
  className,
  sectionStatus,
  prevSectionStatus,
}: {
  style: any;
  className?: string;
  sectionStatus: string;
  prevSectionStatus: string;
}) => {
  const animationFrameId = useRef<number | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);

  const duration = 800;
  const body_to_brain = interpolateAll(body, brain, { single: true });
  const brain_to_body = interpolateAll(brain, body, { single: true });

  const brain_to_resilience = interpolateAll(brain, resilience, {
    single: true,
  });
  const resilience_to_brain = interpolateAll(resilience, brain, {
    single: true,
  });

  const resilience_to_body = interpolateAll(resilience, body, { single: true });
  const body_to_resilience = interpolateAll(body, resilience, { single: true });

  const interPolatorIndexMap = {
    bodymind: 0,
    mindbody: 1,
    mindresilience: 2,
    resiliencemind: 3,
    resiliencebody: 4,
    bodyresilience: 5,
  };

  const interpolators = [
    body_to_brain,
    brain_to_body,
    brain_to_resilience,
    resilience_to_brain,
    resilience_to_body,
    body_to_resilience,
  ];

  const interpolatorIndex =
    interPolatorIndexMap[`${prevSectionStatus}${sectionStatus}`];

  const sigmoid = (t: number, k = 10): number => {
    return 1 / (1 + Math.exp(-k * (t - 0.5)));
  };
  let t = 0;

  const interpolator =
    typeof interpolatorIndex === "undefined" ||
    typeof interpolatorIndex === null
      ? interpolators[0]
      : interpolators[interpolatorIndex];

  useEffect(() => {
    const startTime = performance.now();

    const draw = (time: number) => {
      if (!pathRef.current && !interpolator) return;

      const elapsedTime = time - startTime;
      t = elapsedTime > duration ? 1 : (elapsedTime % duration) / duration;

      const path = interpolator(sigmoid(t, 10));
      const rounded = roundCorners(path, 5);
      pathRef.current?.setAttribute("d", rounded.path);

      if (t < 1) animationFrameId.current = requestAnimationFrame(draw);
    };

    animationFrameId.current = requestAnimationFrame(draw);

    return () => {
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [interpolatorIndex]);

  return interpolator ? (
    <motion.svg
      style={{ x: style.x }}
      xmlns="http://www.w3.org/2000/svg"
      width="45"
      height="45"
      viewBox="-15 -15 100 100"
      fill="white"
      className={className}
    >
      <path ref={pathRef} fill="inherit" xmlns="http://www.w3.org/2000/svg" />
    </motion.svg>
  ) : (
    <BODY className="mr-0 sm:scale-100 lg:scale-125" />
  );
};

const BoutiqueLoadingAnim = ({ className }: { className?: string }) => {
  const animationFrameId = useRef<number | null>(null);
  const pathRef = useRef<SVGPathElement | null>(null);

  const duration = 1500;

  const body_to_brain = interpolateAll(body, brain, { single: true });
  const brain_to_body = interpolateAll(brain, body, { single: true });

  const brain_pause = interpolateAll(brain, brain, { single: true });
  const brain_to_resilience = interpolateAll(brain, resilience, {
    single: true,
  });
  const res_pause = interpolateAll(resilience, resilience, { single: true });
  const resilience_to_body = interpolateAll(resilience, body, { single: true });
  const body_pause = interpolateAll(body, body, { single: true });

  const interpolators = [
    body_to_brain,
    brain_to_resilience,
    resilience_to_body,
  ];

  const sigmoid = (t: number, k = 10): number => {
    return 1 / (1 + Math.exp(-k * (t - 0.5)));
  };

  let t = 0;

  useEffect(() => {
    const startTime = performance.now();

    const draw = (time: number) => {
      if (!pathRef.current) return;

      const elapsedTime = time - startTime;
      t = (elapsedTime % duration) / duration;

      const currentLoopCount = Math.max(0, Math.floor(elapsedTime / duration));
      const interpolator =
        interpolators[currentLoopCount % interpolators.length];
      const path = interpolator(sigmoid(t, 10));
      const rounded = roundCorners(path, 5);
      pathRef.current?.setAttribute("d", rounded.path);

      animationFrameId.current = requestAnimationFrame(draw);
    };

    animationFrameId.current = requestAnimationFrame(draw);

    return () => {
      if (animationFrameId.current !== null) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [duration]);

  return (
    <motion.svg
      key={"continuum-svg"}
      xmlns="http://www.w3.org/2000/svg"
      width="100"
      height="100"
      viewBox="-15 -15 100 100"
      fill="white"
      className={className}
    >
      <path ref={pathRef} fill="inherit" xmlns="http://www.w3.org/2000/svg" />
    </motion.svg>
  );
};

export {
  BoutiqueBodySVG,
  BoutiqueBrainSVG,
  BoutiqueResilienceSVG,
  BoutiqueLogo,
  BoutiqueCircle,
  BoutiqueLogoFull,
  BoutiqueLoadingAnim,
};
